import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Box, Typography, TextField, Button, IconButton, CircularProgress, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles } from './styles';
import { LINK_TYPES } from './constants';

const LinksSection = memo(({ initialData, authAxios, setError, onUpdate }) => {
    const [links, setLinks] = useState(initialData || []);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (initialData) setLinks(initialData);
    }, [initialData]);

    const portfolioLink = useMemo(() => links.find(l => l.link_type === 'PORTFOLIO'), [links]);
    const otherLinks = useMemo(() => links.filter(l => l.link_type !== 'PORTFOLIO'), [links]);

    const addLink = useCallback((linkType = 'OTHER') => {
        setLinks(prev => [...prev, { id: Date.now(), link_type: linkType, url: '', description: '' }]);
    }, []);

    const updateLink = useCallback((id, field, value) => {
        setLinks(prev => prev.map(link => link.id === id ? { ...link, [field]: value } : link));
    }, []);

    const removeLink = useCallback(async (id) => {
        if (typeof id === 'number' && id > 1000000000000) {
            setLinks(prev => prev.filter(link => link.id !== id));
            return;
        }
        try {
            await authAxios.delete(`/links/${id}/`);
            setLinks(prev => prev.filter(link => link.id !== id));
        } catch (error) {
            setError?.('Failed to remove link');
        }
    }, [authAxios, setError]);

    const handleSaveAll = async () => {
        setSaving(true);
        try {
            const savedEntries = await Promise.all(
                links.filter(l => l.url?.trim()).map(async link => {
                    const payload = { link_type: link.link_type, url: link.url, description: link.description };
                    if (typeof link.id === 'number' && link.id > 1000000000000) {
                        return (await authAxios.post('/links/', payload)).data;
                    }
                    return (await authAxios.patch(`/links/${link.id}/`, payload)).data;
                })
            );
            setLinks(savedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            setError?.('Failed to save links');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>Links</Typography>
                <Button variant="contained" onClick={handleSaveAll} disabled={saving}
                    sx={{ bgcolor: '#4f46e5', color: 'white', textTransform: 'none', fontWeight: 500, borderRadius: '6px', px: 3, '&:hover': { bgcolor: '#4338ca' } }}>
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                </Button>
            </Box>

            {/* Portfolio Link */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>Portfolio URL</Typography>
                {portfolioLink ? (
                    <TextField
                        fullWidth size="small" placeholder="https://yourportfolio.com"
                        value={portfolioLink.url || ''}
                        onChange={(e) => updateLink(portfolioLink.id, 'url', e.target.value)}
                        sx={inputStyles}
                        InputProps={{ startAdornment: <LinkIcon sx={{ color: '#9ca3af', mr: 1 }} /> }}
                    />
                ) : (
                    <Button onClick={() => addLink('PORTFOLIO')} startIcon={<AddIcon />}
                        sx={{ color: '#4f46e5', textTransform: 'none', fontWeight: 500 }}>
                        Add Portfolio Link
                    </Button>
                )}
            </Box>

            {/* Other Links */}
            <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#374151', fontSize: '0.875rem' }}>Other Links</Typography>
                {otherLinks.map((link) => (
                    <Box key={link.id} sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                <Select
                                    size="small"
                                    value={link.link_type || 'OTHER'}
                                    onChange={(e) => updateLink(link.id, 'link_type', e.target.value)}
                                    sx={{ minWidth: 140, fontSize: '0.875rem' }}
                                    IconComponent={ExpandMoreIcon}
                                >
                                    {LINK_TYPES.filter(t => t.id !== 'PORTFOLIO').map(type => (
                                        <MenuItem key={type.id} value={type.id}>{type.label}</MenuItem>
                                    ))}
                                </Select>
                                <TextField
                                    fullWidth size="small"
                                    placeholder={LINK_TYPES.find(t => t.id === link.link_type)?.placeholder || 'URL'}
                                    value={link.url || ''}
                                    onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                                    sx={inputStyles}
                                />
                            </Box>
                        </Box>
                        <IconButton size="small" onClick={() => removeLink(link.id)} sx={{ mt: 0.5 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
                <Button onClick={() => addLink('OTHER')} startIcon={<AddIcon />}
                    sx={{ color: '#4f46e5', textTransform: 'none', fontWeight: 500, fontSize: '0.875rem' }}>
                    Add Another Link
                </Button>
            </Box>
        </Box>
    );
});

LinksSection.displayName = 'LinksSection';
export default LinksSection;
